/**
 * AuditDetailDrawer — detalhe forense de um evento (A5).
 *
 * Mostra os 10 campos reais do serializer + payload (mascarado quando
 * `hasSensitive`). Permite abrir a cadeia de auditoria da entidade (cascade
 * ADR-107). Drawer lateral no padrão `UserDetailDrawer` (scrim + aside fixo).
 *
 * Honest-defers (sem endpoint): "Export JSON assinado" e "Adicionar a relatório
 * LGPD" — não renderizados (evita UI morta).
 */

import { useState } from 'react';

import { SEVERITY_BADGE, SEVERITY_LABEL, formatTimestamp, isPayloadMasked } from './audit-labels';
import { AuditCascadeView } from './AuditCascadeView';

import type { AuditEventWire } from './types';

interface Props {
  event: AuditEventWire | null;
  canReadSensitive: boolean;
  onClose: () => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex gap-3 border-b border-border-subtle/60 py-1.5 text-xs">
      <span className="w-28 shrink-0 font-mono text-text-secondary">{label}</span>
      <span className="flex-1 break-all text-text-primary">{children}</span>
    </div>
  );
}

export function AuditDetailDrawer({ event, canReadSensitive, onClose }: Props): JSX.Element | null {
  const [showCascade, setShowCascade] = useState(false);

  if (event === null) return null;

  const masked = isPayloadMasked(event);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-[520px] flex-col bg-surface shadow-2xl"
        role="dialog"
        aria-label="Detalhe do evento de auditoria"
      >
        <div className="flex items-start justify-between border-b border-border-subtle px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-text-primary">{event.eventType}</p>
            <div className="mt-1 flex items-center gap-2">
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${SEVERITY_BADGE[event.severity]}`}
              >
                {SEVERITY_LABEL[event.severity]}
              </span>
              {masked && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                  payload mascarado
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-text-secondary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <section>
            <Field label="id">{event.id}</Field>
            <Field label="ocorreu em">{formatTimestamp(event.occurredAt)}</Field>
            <Field label="tipo">{event.eventType}</Field>
            <Field label="ator">
              {event.actorName !== null && event.actorName !== undefined ? (
                <span title={event.actorUserId ?? undefined}>{event.actorName}</span>
              ) : (
                (event.actorUserId ?? <span className="italic">sistema</span>)
              )}
            </Field>
            <Field label="entidade">
              {event.entityType} · <span className="font-mono">{event.entityId}</span>
            </Field>
            <Field label="severidade">{SEVERITY_LABEL[event.severity]}</Field>
          </section>

          <section className="mt-5">
            <h3 className="mb-2 text-xs font-semibold text-text-primary">Payload</h3>
            {masked ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Payload sensível mascarado pelo servidor. Visualização integral requer{' '}
                <code className="font-mono">audit-events-read-sensitive</code>.
              </div>
            ) : (
              <pre className="overflow-x-auto rounded-md bg-surface-raised p-3 text-[11px] text-text-primary">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            )}
          </section>

          <section className="mt-5">
            <h3 className="mb-2 text-xs font-semibold text-text-primary">Cadeia da entidade</h3>
            {showCascade ? (
              <AuditCascadeView entityId={event.entityId} canReadSensitive={canReadSensitive} />
            ) : (
              <button
                type="button"
                onClick={() => setShowCascade(true)}
                className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-raised"
              >
                Ver cadeia de auditoria desta entidade
              </button>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}
